/** Thrown on a non-2xx response from the S3 POST — carries the status so the
 * caller can distinguish a retryable `403` (size/type/tag/key mismatch, or
 * expired policy — design doc "Error handling") from anything else. */
export class S3UploadError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`S3 upload failed (${status})`);
    this.status = status;
  }
}

/**
 * Phase 2 of the Drop 8 upload flow — POST a file straight to S3 using the
 * presigned-POST `fields` Phase 1 (`begin`) signed. XMLHttpRequest, not
 * `fetch` (design doc "Phase 2 uses XMLHttpRequest, not fetch"): `fetch` has
 * no upload-progress event, and a progress bar is not optional at up to 25 MB.
 *
 * `fields` are replayed verbatim, opaque — every AWS example appends the
 * policy fields before the `file` field (the browser's own multipart
 * serialization order, `FormData` preserves append order), so this mirrors
 * that convention rather than relying on it being unenforced.
 */
export function uploadFileToS3(
  url: string,
  fields: Record<string, string>,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    for (const [key, value] of Object.entries(fields)) form.append(key, value);
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new S3UploadError(xhr.status));
    };
    xhr.onerror = () => reject(new S3UploadError(0));
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(form);
  });
}
